import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Coins } from "lucide-react";
import { toast } from "sonner";
import { getLlmSpend, setLlmSpendConfig } from "@/lib/api";

const usd = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}$`);

export const LlmSpendCard = () => {
  const [d, setD] = useState(null);
  const [thr, setThr] = useState("");
  const [saving, setSaving] = useState(false);
  const ceiling = d?.authorizedCeilingUsd ?? 2;
  const validCap = thr.trim() !== "" && Number.isFinite(Number(thr)) && Number(thr) >= 0 && Number(thr) <= ceiling;
  const load = useCallback(() => getLlmSpend().then(setD).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 300000);
    return () => clearInterval(t);
  }, [load]);

  const save = async () => {
    if (!validCap || saving) return;
    setSaving(true);
    try {
      const r = await setLlmSpendConfig({ dailyCapUsd: Number(thr) });
      setD((p) => ({ ...p, dailyCapUsd: r.dailyCapUsd }));
      toast.success(Number(thr) > 0
        ? `Tetto LLM: ${r.dailyCapUsd}$/giorno UTC, chat incluse`
        : "Chiamate LLM in pausa");
      await load();
    } catch (e) {
      toast.error("Tetto rifiutato");
    } finally {
      setSaving(false);
    }
  };

  if (!d) return null;
  const max = Math.max(...(d.perDay || []).map((x) => x.costUsd), 0.01);
  return (
    <Card className="border-border/70 bg-card/60" data-testid="llm-spend-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          <Coins className="h-4 w-4 text-amber-300" /> Consumi del cervello · chiave universale
          <Badge variant="outline" className="text-[10px] border-border text-muted-foreground"
            data-testid="llm-spend-calls">
            {d.calls} chiamate misurate
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="llm-spend-kpis">
          {[
            ["Oggi", usd(d.todayUsd)],
            [`Media/giorno (${d.fullDays}g pieni)`, usd(d.avgDayUsd)],
            ["Proiezione 30 giorni", usd(d.projected30dUsd)],
            [d.blockedToday ? `Tetto/giorno · ${d.blockedToday} bloccate` : "Tetto/giorno",
              d.dailyCapUsd ? usd(d.dailyCapUsd) : "pausa"],
          ].map(([k, v], i) => (
            <div key={k} className="rounded-md border border-border/60 p-2 text-center" data-testid={`llm-spend-kpi-${i}`}>
              <div className="font-mono text-base text-white/85">{v}</div>
              <div className="text-[10px] text-muted-foreground">{k}</div>
            </div>
          ))}
        </div>

        {d.budget && <p className="text-[11px] text-amber-200 break-words" data-testid="llm-spend-reservations">
          Riservato: {usd(d.budget.reservedUsd)} · Disponibile: {usd(d.budget.remainingUsd)}
          {d.budget.uncertain ? " · BLOCCATO: costo storico incerto" : ""}
        </p>}

        {(d.perDay || []).length > 0 && (
          <div className="flex items-end gap-px h-10" data-testid="llm-spend-curve">
            {(d.perDay || []).slice(-30).map((x) => (
              <div key={x.day} title={`${x.day}: ${x.costUsd}$ · ${x.calls} chiamate`}
                data-testid={`llm-spend-day-${x.day}`}
                className="flex-1 min-w-[3px] rounded-sm bg-amber-500/50"
                style={{ height: `${Math.max((x.costUsd / max) * 100, 4)}%` }} />
            ))}
          </div>
        )}

        <div className="space-y-1" data-testid="llm-spend-sources">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Chi spende
          </p>
          {(d.perSource || []).slice(0, 6).map((s) => (
            <div key={s.source} className="flex flex-wrap items-center gap-x-2 font-mono text-[11px]"
              data-testid={`llm-spend-source-${s.source}`}>
              <span className="w-40 truncate text-white/70">{s.source}</span>
              <span className="text-amber-300/90">{usd(s.costUsd)}</span>
              <span className="text-white/35">{s.calls} chiamate</span>
              <span className="text-white/25 truncate">
                {(s.tokensIn / 1000).toFixed(0)}k in · {(s.tokensOut / 1000).toFixed(0)}k out
              </span>
            </div>
          ))}
          {!(d.perSource || []).length && (
            <p className="text-[11px] text-muted-foreground" data-testid="llm-spend-empty">
              Nessuna chiamata ancora misurata: il contatore parte da adesso.
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {(d.perModel || []).map((m) => (
            <Badge key={m.model} variant="outline"
              className="text-[10px] border-border/70 text-muted-foreground"
              data-testid={`llm-spend-model-${m.model}`}>
              {m.model} {usd(m.costUsd)}
            </Badge>
          ))}
          <div className="ml-auto flex flex-wrap items-center gap-1.5 max-w-full">
            <Input value={thr} onChange={(e) => setThr(e.target.value)}
              type="number" min="0" max={ceiling} step="0.01" aria-label="Tetto LLM giornaliero in dollari"
              placeholder={`tetto $/giorno (ora ${d.dailyCapUsd})`}
              className="h-7 w-44 text-[11px]" data-testid="llm-spend-threshold" />
            <Button size="sm" variant="outline" className="h-7 text-[11px]"
              disabled={!validCap || saving}
              onClick={save} data-testid="llm-spend-save">Imposta tetto</Button>
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground/70 leading-relaxed break-words" data-testid="llm-spend-policy">
          Tetto autorizzato {ceiling}$/giorno UTC. Gli altri agenti restano entro i primi 2$ condivisi. Zero = pausa. Il costo massimo viene
          riservato prima della richiesta; gli esiti incerti restano impegnati.
          {(d.perHost || []).length > 1 && (
            <b className="text-amber-300" data-testid="llm-spend-hosts"> {d.perHost.length} host
              osservati nel periodo: non prova di esecuzione simultanea.</b>
          )}
          {" "}{d.copertura} {d.senzaTariffa?.length
            ? `Modelli senza tariffa dichiarata (costo non sommato): ${d.senzaTariffa.join(", ")}.`
            : ""} {d.nota}
        </p>
      </CardContent>
    </Card>
  );
};

export default LlmSpendCard;
